import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Badge from '../components/Badge';
import Button from '../components/Button';
import QrCode, { buildEpcQr } from '../components/QrCode';

function fmt(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
      <div>
        <p className="text-xs text-slate-400 leading-none mb-0.5">{label}</p>
        <p className="font-mono text-sm font-bold text-ocean-800">{value}</p>
      </div>
      <button onClick={copy} className="text-xs text-ocean-600 hover:text-ocean-800 font-medium shrink-0 transition-colors">
        {copied ? '✓ Kopiert' : 'Kopieren'}
      </button>
    </div>
  );
}

function PaymentSection({ ticket, refInputs, setRefInputs, messages, onSubmit }) {
  const hasPaypal = !!(ticket.event_paypal_info || ticket.event_paypal_me);
  const hasBank = !!(ticket.event_bank_enabled !== false && (ticket.event_bank_iban || ticket.event_bank_info));
  const hasBoth = hasPaypal && hasBank;

  const [method, setMethod] = useState(hasPaypal ? 'paypal' : 'bank');

  const paypalLink = ticket.event_paypal_me && ticket.price
    ? `https://paypal.me/${ticket.event_paypal_me}/${parseFloat(ticket.price).toFixed(2)}`
    : null;

  return (
    <div className="alert-warning mb-3">
      <p className="font-semibold mb-1">Zahlung ausstehend</p>
      <p className="text-sm text-amber-700 mb-3">
        Überweise den Betrag und trage anschließend deine Referenz ein, damit wir die Zahlung zuordnen können.
      </p>

      {/* Verwendungszweck */}
      <div className="mb-3">
        <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1.5">Verwendungszweck</p>
        <CopyField label="Bei jeder Zahlungsart angeben" value={ticket.ticket_code} />
      </div>

      {/* Method tabs */}
      {hasBoth && (
        <div className="flex gap-1 mb-3">
          <button onClick={() => setMethod('paypal')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${method === 'paypal' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}>
            PayPal
          </button>
          <button onClick={() => setMethod('bank')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${method === 'bank' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-800 hover:bg-amber-200'}`}>
            Banküberweisung
          </button>
        </div>
      )}

      {/* PayPal */}
      {method === 'paypal' && hasPaypal && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">PayPal</p>
          <div className="flex flex-col sm:flex-row gap-3 items-start">
            <div className="flex-1 space-y-2">
              {paypalLink && (
                <a href={paypalLink} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-[#0070ba] hover:bg-[#005ea6] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors no-underline w-fit">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082H9.825l-1.43 9.067h3.44a.64.64 0 0 0 .631-.548l.026-.139.502-3.18.032-.174a.64.64 0 0 1 .632-.547h.398c2.576 0 4.594-.543 5.684-1.809.964-1.1 1.272-2.425.982-4.465z"/></svg>
                  Jetzt {parseFloat(ticket.price).toFixed(2)} € via PayPal zahlen
                </a>
              )}
              {ticket.event_paypal_info && (
                <pre className="text-sm text-amber-900 whitespace-pre-wrap font-sans">{ticket.event_paypal_info}</pre>
              )}
            </div>
            {paypalLink && (
              <div className="text-center shrink-0">
                <QrCode value={paypalLink} size={112} className="border border-amber-200 rounded-lg" />
                <p className="text-xs text-amber-700 mt-1">QR-Code scannen</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bank */}
      {method === 'bank' && hasBank && (() => {
        const iban = (ticket.event_bank_iban || '').replace(/\s/g, '');
        const epcValue = iban ? buildEpcQr({
          iban,
          bic: ticket.event_bank_bic || '',
          holder: ticket.event_bank_holder || '',
          amount: ticket.price || '',
          reference: ticket.ticket_code,
        }) : null;
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Banküberweisung</p>
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              <div className="flex-1 space-y-1.5">
                {ticket.event_bank_holder && <CopyField label="Kontoinhaber" value={ticket.event_bank_holder} />}
                {ticket.event_bank_iban && <CopyField label="IBAN" value={ticket.event_bank_iban} />}
                {ticket.event_bank_bic && <CopyField label="BIC" value={ticket.event_bank_bic} />}
                {ticket.event_bank_info && (
                  <pre className="text-sm text-amber-900 whitespace-pre-wrap font-sans mt-1">{ticket.event_bank_info}</pre>
                )}
              </div>
              {epcValue && (
                <div className="text-center shrink-0">
                  <QrCode value={epcValue} size={112} className="border border-amber-200 rounded-lg" />
                  <p className="text-xs text-amber-700 mt-1">EPC-QR scannen</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Reference input */}
      <div>
        <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1.5">Zahlungsreferenz eintragen</p>
        <p className="text-xs text-amber-700 mb-2">
          {method === 'bank'
            ? 'Trage hier das Datum deiner Überweisung oder eine Referenznummer ein.'
            : 'Trage hier deine PayPal-Transaktions-ID ein.'}
        </p>
        <div className="flex gap-2">
          <input className="input text-sm flex-1"
            placeholder={method === 'bank' ? 'z.B. Überwiesen am 10.06.2026' : 'PayPal Transaktions-ID'}
            value={refInputs[ticket.id] || ticket.payment_reference || ''}
            onChange={(e) => setRefInputs({ ...refInputs, [ticket.id]: e.target.value })} />
          <Button size="sm" variant="sand" onClick={() => onSubmit(ticket.id)}>Speichern</Button>
        </div>
        {messages[ticket.id] && <p className="text-xs text-emerald-700 mt-1.5">{messages[ticket.id]}</p>}
      </div>
    </div>
  );
}

function WalletButtons({ ticket }) {
  const [config, setConfig] = useState(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);

  useEffect(() => {
    api.get('/api/wallet-config').then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const openGoogle = async () => {
    setGoogleLoading(true);
    try {
      const r = await api.get(`/api/tickets/${ticket.id}/google-wallet`);
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch { alert('Google Wallet nicht verfügbar.'); }
    finally { setGoogleLoading(false); }
  };

  const downloadApple = async () => {
    setAppleLoading(true);
    try {
      const r = await api.get(`/api/tickets/${ticket.id}/apple-wallet`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.apple.pkpass' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticket.ticket_code}.pkpass`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Apple Wallet nicht verfügbar.'); }
    finally { setAppleLoading(false); }
  };

  if (!config || (!config.google_wallet && !config.apple_wallet)) return null;

  return (
    <div className="flex gap-2 flex-wrap mt-2">
      {config.google_wallet && (
        <button onClick={openGoogle} disabled={googleLoading}
          className="flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50">
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#4285F4"/>
            <path d="M17.6 12.2c0-.4-.04-.8-.1-1.2H12v2.3h3.1c-.1.8-.7 1.9-1.8 2.4v2h2.9c1.7-1.6 2.4-3.9 2.4-5.5z" fill="#fff"/>
            <path d="M12 18c1.6 0 2.9-.5 3.9-1.4l-2.9-2c-.5.3-1.2.5-2 .5-1.5 0-2.8-1-3.3-2.4H5.7v2.1C6.7 16.9 9.2 18 12 18z" fill="#fff"/>
            <path d="M8.7 12.7c-.1-.4-.2-.8-.2-1.2s.1-.8.2-1.2V8.2H5.7C5.2 9.1 5 10 5 11s.2 1.9.7 2.8l3-2.1z" fill="#fff"/>
            <path d="M12 7.8c.9 0 1.7.3 2.3.9l1.7-1.7C14.9 6 13.6 5.4 12 5.4c-2.8 0-5.3 1.1-6.3 3.2l3 2.1c.5-1.4 1.8-2.4 3.3-2.9z" fill="#fff"/>
          </svg>
          <span className="text-xs font-medium text-slate-700">{googleLoading ? '…' : 'Google Wallet'}</span>
        </button>
      )}
      {config.apple_wallet && (
        <button onClick={downloadApple} disabled={appleLoading}
          className="flex items-center gap-2 bg-black hover:bg-zinc-800 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50">
          <svg className="w-4 h-4 fill-white" viewBox="0 0 814 1000"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 438.9 31.1 273.5 64.3 194.1c32.4-77.3 98.3-126 173.4-126 67.8 0 110.4 45.8 163.9 45.8 51.7 0 83.7-45.8 164-45.8 66.5 0 130.3 45.4 164.4 111.9zm-101.3-87.8c-9-32.4-24.5-70.5-53.1-94.8-23.7-20.4-52.7-34.7-84.1-34.7-2.9 0-5.8.3-8.7.6 28.2 37.3 45.8 78.8 45.8 124.6v5.8z"/></svg>
          <span className="text-xs font-medium text-white">{appleLoading ? '…' : 'Apple Wallet'}</span>
        </button>
      )}
    </div>
  );
}

export default function MyTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refInputs, setRefInputs] = useState({});
  const [messages, setMessages] = useState({});
  const [bingoActive, setBingoActive] = useState(false);
  const [bingoCards, setBingoCards] = useState([]);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState('');
  const [bookMessage, setBookMessage] = useState('');

  const loadTickets = () => api.get('/api/tickets/my').then((r) => setTickets(r.data));

  useEffect(() => {
    loadTickets().finally(() => setLoading(false));
    api.get('/api/events/current').then((r) => setCurrentEvent(r.data)).catch(() => setCurrentEvent(null));
    api.get('/api/bingo/settings').then((r) => {
      if (r.data.is_active) {
        setBingoActive(true);
        api.get('/api/bingo/my-cards').then((r2) => setBingoCards(r2.data)).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const bookTicket = async () => {
    setBooking(true); setBookError(''); setBookMessage('');
    try {
      await api.post('/api/tickets', { event_id: currentEvent.id });
      setBookMessage('Ticket gebucht! Überweise jetzt den Betrag und trage die Referenz unten ein.');
      await loadTickets();
    } catch (err) { setBookError(err.response?.data?.detail || 'Fehler beim Buchen'); }
    finally { setBooking(false); }
  };

  const hasCurrentEventTicket = currentEvent && tickets.some((t) => t.event_id === currentEvent.id && t.status !== 'cancelled');

  const submitRef = async (ticketId) => {
    try {
      await api.put(`/api/tickets/${ticketId}/payment-reference`, { payment_reference: refInputs[ticketId] });
      setMessages({ ...messages, [ticketId]: 'Referenz gespeichert!' });
      const r = await api.get('/api/tickets/my');
      setTickets(r.data);
    } catch (err) { setMessages({ ...messages, [ticketId]: err.response?.data?.detail || 'Fehler' }); }
  };

  const downloadPdf = async (ticket) => {
    try {
      const res = await api.post(`/api/tickets/${ticket.id}/pdf`, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `ticket-${ticket.ticket_code}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('PDF-Download fehlgeschlagen'); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent"/></div>;

  return (
    <div>
      <h1 className="page-title">Tickets</h1>

      {currentEvent && currentEvent.status === 'ticket_sale_active' && !hasCurrentEventTicket && (
        <div className="card border border-ocean-200 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-bold text-ocean-900">{currentEvent.name}</p>
            <p className="text-sm text-slate-500">
              Ticketverkauf läuft{currentEvent.ticket_price ? ` · ${parseFloat(currentEvent.ticket_price).toFixed(2)} €` : ' · Kostenlos'}
            </p>
          </div>
          <Button onClick={bookTicket} loading={booking}>Ticket kaufen</Button>
        </div>
      )}
      {bookMessage && <div className="alert-success mb-4">{bookMessage}</div>}
      {bookError && <div className="alert-error mb-4">{bookError}</div>}

      {tickets.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-4xl mb-3">🎟️</div>
          <p className="text-slate-500 mb-4">Du hast noch keine Tickets.</p>
          <Link to="/" className="no-underline btn-primary">Zum aktuellen Event</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((t) => (
            <div key={t.id} className={`card border-l-4 ${t.status === 'unlocked' || t.status === 'checked_in' ? 'border-l-emerald-400' : t.status === 'cancelled' ? 'border-l-red-300' : 'border-l-sand-400'}`}>
              <div className="flex flex-wrap justify-between gap-3 mb-4">
                <div>
                  <p className="font-bold text-ocean-900 text-base">{t.event_name || `Event #${t.event_id}`}</p>
                  <p className="font-mono text-xs text-slate-400 mt-0.5">#{t.ticket_code} · {fmt(t.created_at)}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Badge status={t.status} />
                  <Badge status={t.payment_status} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                {t.price && <div className="bg-sand-50 rounded-lg px-3 py-2 text-center"><p className="text-xs text-slate-500">Preis</p><p className="font-bold text-sand-700">{parseFloat(t.price).toFixed(2)} €</p></div>}
                {t.seat_label && <div className="bg-ocean-50 rounded-lg px-3 py-2 text-center"><p className="text-xs text-slate-500">Sitzplatz</p><p className="font-bold text-ocean-700">💺 {t.seat_label}</p></div>}
              </div>

              {t.admin_comment && <div className="alert-info mb-3 text-xs">💬 <b>Admin:</b> {t.admin_comment}</div>}

              {t.status === 'pending_payment' && (
                <PaymentSection ticket={t} refInputs={refInputs} setRefInputs={setRefInputs} messages={messages} onSubmit={submitRef} />
              )}

              {(t.status === 'unlocked' || t.status === 'checked_in') && (
                <div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="secondary" onClick={() => downloadPdf(t)}>📄 PDF herunterladen</Button>
                    {t.status === 'unlocked' && (
                      <Link to={`/events/${t.event_id}/seats`} className="no-underline btn btn-sm bg-reef-500 text-white hover:bg-reef-600">💺 Sitzplatz wählen</Link>
                    )}
                    {bingoActive && bingoCards.find((c) => c.ticket_id === t.id) && (
                      <Button size="sm" variant="sand"
                        onClick={() => navigate(`/bingo/${bingoCards.find((c) => c.ticket_id === t.id).token}`)}>
                        🎱 Bingo-Karte
                      </Button>
                    )}
                  </div>
                  <WalletButtons ticket={t} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
